import React, { useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import { Toaster } from './components/ui/sonner';
import { AuthProvider } from './contexts/AuthContext';
import { SettingsProvider } from './contexts/SettingsContext';
import ProtectedRoute from './components/ProtectedRoute';
import WhatsAppFloat from './components/WhatsAppFloat';
import NetworkBackground from './components/NetworkBackground';
import SkipToContent from './components/SkipToContent';

// Public Pages
import Home from './pages/Home';
import AgendaPage from './pages/AgendaPage';
import TeamPage from './pages/TeamPage';
import BecomeFranchisee from './pages/BecomeFranchisee';
import JoinTeam from './pages/JoinTeam';
import EneaflixPage from './pages/EneaflixPage';
import BlogPage from './pages/BlogPage';
import BlogPost from './pages/BlogPost';
import EneaflixDetail from './pages/EneaflixDetail';
import SolutionsIndividual from './pages/SolutionsIndividual';
import SolutionsCorporate from './pages/SolutionsCorporate';
import ProductPage from './pages/ProductPage';
import Contact from './pages/Contact';
import FranchisePage from './pages/FranchisePage';
import FranchiseLocatorPage from './pages/FranchiseLocatorPage';

// Admin Pages
import Login from './pages/admin/Login';
import AdminLayout from './pages/admin/AdminLayout';
import Dashboard from './pages/admin/Dashboard';
import FranchisesManager from './pages/admin/FranchisesManager';
import BlogManager from './pages/admin/BlogManager';
import { TestimonialsManager, FAQManager, EneaflixManager } from './pages/admin/ContentManager';
import MediaManager from './pages/admin/MediaManager';
import EventsManager from './pages/admin/EventsManager';
import TeamManager from './pages/admin/TeamManager';
import SubmissionsManager from './pages/admin/SubmissionsManager';
import SystemDiagnostics from './pages/admin/SystemDiagnostics';

import './App.css';

function App() {
  useEffect(() => {
    // Mark JS as ready so CSS can safely hide animate-on-scroll elements
    // until the IntersectionObserver activates them. Falls back to "visible"
    // when JS is disabled.
    document.documentElement.classList.add('js-ready');

    // Global IntersectionObserver — redundancy for elements whose owning
    // component fails to wire up an observer (e.g., on route changes).
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            e.target.classList.add('active');
            observer.unobserve(e.target);
          }
        });
      },
      { threshold: 0.05, rootMargin: '0px 0px -40px 0px' },
    );

    const scan = () => {
      document
        .querySelectorAll('.animate-on-scroll:not(.active)')
        .forEach((el) => observer.observe(el));
    };
    scan();
    // Re-scan periodically for newly-mounted elements (route changes, lazy data).
    const interval = setInterval(scan, 1500);

    // Safety net: after 4s, force any still-hidden element to .active to avoid blank sections.
    const safety = setTimeout(() => {
      document
        .querySelectorAll('.animate-on-scroll:not(.active)')
        .forEach((el) => {
          const rect = el.getBoundingClientRect();
          if (rect.top < window.innerHeight && rect.bottom > 0) {
            el.classList.add('active');
          }
        });
    }, 4000);

    return () => {
      observer.disconnect();
      clearInterval(interval);
      clearTimeout(safety);
    };
  }, []);

  return (
    <HelmetProvider>
      <AuthProvider>
        <SettingsProvider>
          <BrowserRouter>
          {/* Skip to content for keyboard / screen reader users */}
          <SkipToContent />
          {/* Animated network background (public pages only) */}
          <NetworkBackground />
          <Routes>
            {/* Public Routes */}
            <Route path="/" element={<Home />} />
            <Route path="/agenda" element={<AgendaPage />} />
            <Route path="/equipe" element={<TeamPage />} />
            <Route path="/franqueado" element={<BecomeFranchisee />} />
            <Route path="/seja-franqueado" element={<BecomeFranchisee />} />
            <Route path="/franquias" element={<FranchiseLocatorPage />} />
            <Route path="/franquia/:id" element={<FranchisePage />} />
            <Route path="/trabalhe-conosco" element={<JoinTeam />} />
            <Route path="/eneaflix" element={<EneaflixPage />} />
            <Route path="/eneaflix/:id" element={<EneaflixDetail />} />
            <Route path="/blog" element={<BlogPage />} />
            <Route path="/blog/:slug" element={<BlogPost />} />
            <Route path="/solucoes-individuais" element={<SolutionsIndividual />} />
            <Route path="/solucoes-empresariais" element={<SolutionsCorporate />} />
            {/* Product subpages — one per IE program */}
            <Route path="/e-personalidade" element={<ProductPage />} />
            <Route path="/e-processual" element={<ProductPage />} />
            <Route path="/e-corpo" element={<ProductPage />} />
            <Route path="/e-vendas" element={<ProductPage />} />
            <Route path="/e-got" element={<ProductPage />} />
            <Route path="/contato" element={<Contact />} />

            {/* Admin Auth Routes */}
            <Route path="/admin/login" element={<Login />} />

            {/* Protected Admin Routes */}
            <Route
              path="/admin"
              element={
                <ProtectedRoute>
                  <AdminLayout />
                </ProtectedRoute>
              }
            >
              <Route index element={<Navigate to="/admin/dashboard" replace />} />
              <Route path="dashboard" element={<Dashboard />} />
              <Route path="franchises" element={<FranchisesManager />} />
              <Route path="events" element={<EventsManager />} />
              <Route path="team" element={<TeamManager />} />
              <Route path="blog" element={<BlogManager />} />
              <Route path="testimonials" element={<TestimonialsManager />} />
              <Route path="faq" element={<FAQManager />} />
              <Route path="eneaflix" element={<EneaflixManager />} />
              <Route path="submissions" element={<SubmissionsManager />} />
              <Route path="media" element={<MediaManager />} />
              <Route path="system" element={<SystemDiagnostics />} />
            </Route>

            {/* 404 */}
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
          <WhatsAppFloat />
        </BrowserRouter>
        <Toaster position="top-right" />
        </SettingsProvider>
      </AuthProvider>
    </HelmetProvider>
  );
}

export default App;
